<template>
  <b-card v-if="!!option" id="page-product-form" no-body class="mb-0">
    <b-card-header>
      <b-card-title>
        <h2>
          {{ $t(getTitle()) }} {{ $t("pages.products.title").toLowerCase() }}
        </h2>
      </b-card-title>
      <b-button variant="primary" @click="goBack">
        <feather-icon icon="ArrowLeftIcon" />
        {{ $t("back") }}
      </b-button>
    </b-card-header>

    <b-card-body>
      <form-wizard
        ref="form"
        color="#44b8ff"
        :title="null"
        :subtitle="null"
        shape="square"
        class="mb-3 shadow-none"
      >
        <tab-content
          :title="$t('pages.products.title')"
          :before-change="validationProductTab"
        >
          <validation-observer ref="productRules" tag="form">
            <b-form>
              <b-row>
                <!-- Nombre -->
                <b-col cols="12" md="6">
                  <b-form-group :label="$t(label('name'))" label-for="name">
                    <validation-provider
                      #default="{ errors }"
                      :name="$t(label('name'))"
                      rules="required"
                    >
                      <b-form-input
                        id="name"
                        v-model="product.name"
                        :disabled="option === 2 ? true : false"
                        :state="errors.length > 0 ? false : null"
                        :placeholder="$t(label('name'))"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Tipo de Producto -->
                <b-col cols="12" md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('typeProd'))"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t(label('typeProd'))"
                      label-for="typeProd"
                      :state="errors.length > 0 ? false : null"
                    >
                      <v-select
                        v-model="product.stringCategoriasTags"
                        input-id="typeProd"
                        :options="listCategories"
                        label="name"
                        :multiple="true"
                        :disable-on-close="true"
                        :placeholder="$t(label('typeProd'))"
                        :disabled="!isSaveMode"
                        :reduce="(option) => option.name"
                      >
                        <span slot="no-options">{{ $t("sorryOption") }}</span>
                      </v-select>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Organización -->
                <b-col cols="12" md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('organization'))"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t(label('organization'))"
                      label-for="idCatFamcosTypeColegioCorporativo"
                      :state="errors.length > 0 ? false : null"
                    >
                      <v-select
                        input-id="idCatFamcosTypeColegioCorporativo"
                        v-model="product.organizationObj"
                        :options="listTypeProd"
                        label="name"
                        :placeholder="$t(label('organization'))"
                        :disabled="!isSaveMode"
                        :reduce="(option) => option"
                        @input="organizationChanged"
                      >
                        <span slot="no-options">{{ $t("sorryOption") }}</span>
                      </v-select>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col v-if="!isSchoolDisabled" cols="12" md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('schools'))"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t(label('schools'))"
                      label-for="colegios"
                      :state="errors.length > 0 ? false : null"
                    >
                      <v-select
                        :key="product.guid || 'new-product'"
                        input-id="colegios"
                        v-model="product.colegios"
                        :options="famcosColegiosList"
                        label="nombre"
                        multiple
                        :close-on-select="false"
                        :placeholder="$t(label('schools'))"
                        :disabled="!isSaveMode"
                        :reduce="(option) => option.idColegio"
                        :filterable="true"
                        :loading="schoolLoading"
                      >
                        <span slot="no-options">{{ $t("sorryOption") }}</span>
                      </v-select>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col cols="12" md="6">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t(label('productCondition'))"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t(label('productCondition'))"
                      label-for="productCondition"
                      :state="errors.length > 0 ? false : null"
                    >
                      <v-select
                        v-model="product.idCatCondicion"
                        input-id="productCondition"
                        :options="prodCondition"
                        label="name"
                        :placeholder="$t(label('productCondition'))"
                        :disabled="!isSaveMode"
                        :reduce="(option) => option.id"
                      >
                        <span slot="no-options">{{ $t("sorryOption") }}</span>
                      </v-select>
                      <b-form-invalid-feedback
                        :state="errors.length > 0 ? false : null"
                      >
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group
                    :label="$t(label('price_range'))"
                    label-for="priceByRange"
                  >
                    <b-form-checkbox
                      id="priceByRange"
                      v-model="product.priceByRange"
                      switch
                      inline
                      @input.native="changeRange($event)"
                      :disabled="!isSaveMode"
                    >
                      {{ isPriceByRange(product.priceByRange) }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>

                <b-col v-if="!product.priceByRange" cols="12" md="6">
                  <b-form-group :label="$t(label('price'))" label-for="price">
                    <validation-provider
                      #default="{ errors }"
                      :name="$t(label('price'))"
                      rules="required"
                    >
                      <b-input-group
                        :prepend="divisa"
                        append=".00"
                        class="input-group-merge"
                      >
                        <b-form-input
                          id="price"
                          v-model="product.price"
                          type="number"
                          :disabled="option === 2 ? true : false"
                          :state="errors.length > 0 ? false : null"
                          :placeholder="$t(label('price'))"
                        />
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col v-if="!product.priceByRange" cols="12" md="6">
                  <b-form-group
                    :label="$t(label('priceDivisa'))"
                    label-for="priceDivisa"
                  >
                    <b-input-group
                      :prepend="divisa"
                      append=".00"
                      class="input-group-merge"
                    >
                      <b-form-input
                        id="priceDivisa"
                        v-model="product.priceDivisa"
                        type="number"
                        :disabled="option === 2 ? true : false"
                        :placeholder="$t(label('priceDivisa'))"
                      />
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col v-if="product.priceByRange" cols="12" md="6">
                  <b-form-group
                    :label="$t(label('price_from'))"
                    label-for="priceFrom"
                  >
                    <b-input-group
                      :prepend="divisa"
                      append=".00"
                      class="input-group-merge"
                    >
                      <b-form-input
                        id="priceFrom"
                        v-model="product.priceFrom"
                        type="number"
                        :disabled="option === 2 ? true : false"
                        :placeholder="$t(label('price_from'))"
                      />
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col v-if="product.priceByRange" cols="12" md="6">
                  <b-form-group
                    :label="$t(label('price_to'))"
                    label-for="priceTo"
                  >
                    <b-input-group
                      :prepend="divisa"
                      append=".00"
                      class="input-group-merge"
                    >
                      <b-form-input
                        id="priceTo"
                        v-model="product.priceTo"
                        type="number"
                        :disabled="option === 2 ? true : false"
                        :placeholder="$t(label('price_to'))"
                      />
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group
                    :label="$t(label('generate_appointment'))"
                    label-for="generateAppointment"
                  >
                    <b-form-checkbox
                      id="generateAppointment"
                      v-model="product.generateAppointment"
                      switch
                      inline
                      @input.native="changeGenerateAppointment($event)"
                      :disabled="!isSaveMode"
                    >
                      {{ isGenerateAppointment(product.generateAppointment) }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>

                <b-col v-if="product.generateAppointment" cols="12" md="6">
                  <b-form-group
                    :state="durationMinutesState"
                    :label="$t(label('duration_minutes'))"
                    label-for="durationMinutes"
                  >
                    <b-form-input
                      id="durationMinutes"
                      v-model="product.durationMinutes"
                      type="text"
                      :disabled="option === 2 ? true : false"
                      :placeholder="$t(label('duration_minutes'))"
                      @input="onInput"
                      @blur="onBlur"
                      :state="durationMinutesState"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group
                    :state="stockState"
                    :label="$t(label('stock'))"
                    label-for="stock"
                  >
                    <b-form-input
                      id="stock"
                      v-model="product.stock"
                      type="text"
                      :disabled="option === 2 ? true : false"
                      :placeholder="$t(label('stock'))"
                      @input="onInput"
                      @blur="onBlur"
                      :state="stockState"
                    />
                  </b-form-group>
                </b-col>
                <!-- COLUMNA  @input="validateStock"-->

                <b-col cols="12" md="6">
                  <b-form-group
                    :label="$t(label('description'))"
                    label-for="description"
                  >
                    <b-form-textarea
                      id="description"
                      v-model="product.description"
                      :placeholder="$t(label('description'))"
                      :disabled="!isSaveMode"
                      rows="6"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group
                    :label="$t(label('details'))"
                    label-for="details"
                  >
                    <b-form-textarea
                      id="details"
                      v-model="product.details"
                      :placeholder="$t(label('details'))"
                      :disabled="!isSaveMode"
                      rows="6"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </tab-content>

        <tab-content :title="$t('pages.products.photos')" ref="formList">
          <b-row>
            <b-col v-if="option != 2" sm="6">
              <b-media class="body-style">
                <div>
                  <input
                    id="inputFilePhoto"
                    ref="fileInputPhoto"
                    name="inputFilePhoto"
                    type="file"
                    class="hidden"
                    accept=".png,.peg,.jpg,.jpeg"
                    @change="onFileChange($event, 'photos')"
                  />
                  <div
                    class="cursor-pointer m-auto cuve text-center border-2 d-flex align-items-center justify-content-center"
                    accept=".png,.peg,.jpg,.jpeg"
                    @drop="handleDrop($event, 'photos')"
                    @dragover="handleDragover"
                    @dragenter="handleDragover"
                    @click="$refs.fileInputPhoto.click()"
                  >
                    <feather-icon
                      icon="UploadCloudIcon"
                      size="20"
                      svg-classes="h-16 w-16 stroke-current text-grey"
                    />
                    <span
                      style="margin-left: 5px"
                      @click.stop="$refs.fileInputPhoto.click()"
                      >Subir imagen
                    </span>
                  </div>
                </div>
              </b-media>
            </b-col>
            <b-col
              v-for="(item, index) in product.photos"
              :key="index"
              cols="12"
              sm="12"
              md="12"
              lg="6"
              xl="6"
              class="d-flex justify-content-center"
            >
              <b-media class="body-style">
                <template #aside>
                  <img
                    id="image"
                    ref="image"
                    :src="item.urlPhoto"
                    class="img-avatar border-dashed"
                  />
                </template>
                <div
                  v-if="product.photos && option != 2"
                  class="d-flex flex-wrap"
                  style="width: 10px"
                >
                  <b-button
                    variant="primary"
                    style="height: 30px; width: 30px; padding: 4px"
                    @click="deleteFile(item, index)"
                  >
                    <feather-icon icon="XIcon" size="21" />
                  </b-button>
                </div>
              </b-media>
            </b-col>
          </b-row>
        </tab-content>

        <template slot="footer" slot-scope="props">
          <div class="wizard-footer-left">
            <wizard-button
              v-if="props.activeTabIndex > 0 && !props.isLastStep"
              @click.native="props.prevTab()"
              :style="props.fillButtonStyle"
            >
              {{ $t("back") }}
            </wizard-button>

            <wizard-button
              v-else-if="props.activeTabIndex > 0 && props.isLastStep"
              @click.native="props.prevTab()"
              :style="props.fillButtonStyle"
            >
              {{ $t("back") }}
            </wizard-button>
          </div>
          <div class="wizard-footer-right">
            <wizard-button
              v-if="!props.isLastStep"
              @click.native="props.nextTab()"
              class="wizard-footer-right"
              :style="props.fillButtonStyle"
            >
              {{ $t("following") }}
            </wizard-button>

            <wizard-button
              v-else-if="option != 2"
              @click.native="onSubmit"
              class="wizard-footer-right finish-button"
              :style="props.fillButtonStyle"
            >
              {{ props.isLastStep ? $t("save") : $t("following") }}
            </wizard-button>
          </div>
        </template>
      </form-wizard>
    </b-card-body>
  </b-card>
</template>

<script>
import _ from "lodash";
import { heightTransition } from "@core/mixins/ui/transition";
import { FormWizard, TabContent, WizardButton } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import {
  BInputGroup,
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormGroup,
  BFormCheckbox,
  BForm,
  BFormTextarea,
  BToast,
  BMedia,
  BFormInvalidFeedback,
} from "bootstrap-vue";
import router from "@/router";
import Ripple from "vue-ripple-directive";
import { showLoading, stopLoading, getParams } from "@core/utils/utils";
import { required, integer } from "@validations";
import vSelect from "vue-select";
import {
  getProduct,
  createOrEditProduct,
  categoryProductsByCompany,
} from "@core/api/request/products";
import { allFamcosColegios } from "@/@core/api/request/schoolsFamcos";
import { getCatalog, postFile } from "@core/api/request/catalogs";
import { deleteCookie, getCookie, setCookie } from "@/auth/CookiesManager";

const productTemplate = {
  guid: null,
  guidCompany: null,
  name: null,
  description: null,
  details: null,
  price: 0,
  priceDivisa: 0,
  idCatCategory: null,
  stock: "0",
  idIva: null,
  idCatCondicion: null,
  stringCategoriasTags: null,
  colegios: [],
  folio: null,
  photos: [],
  priceByRange: false,
  priceFrom: 0,
  priceTo: 0,
  idFamcosColegio: null,
  idCatFamcosTypeColegioCorporativo: null,
  durationMinutes: 0,
  generateAppointment: false,
  organizationObj: null,
};

export default {
  components: {
    BInputGroup,
    FormWizard,
    TabContent,
    WizardButton,
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    vSelect,
    BFormTextarea,
    BToast,
    BFormInvalidFeedback,
    BMedia,
  },

  directives: {
    Ripple,
  },
  mixins: [heightTransition],
  data: () => ({
    isSaveMode: true,
    required,
    integer,
    product: Object.assign(productTemplate),
    option: "",
    listCategories: [],
    items: [],
    stockValid: true,
    durationMinutesValid: true,
    guidCompany: null,
    prodCondition: [
      {
        id: null,
        name: "",
        color: null,
      },
    ],
    famcosColegiosList: [],
    schoolRows: 999,
    schoolLoading: false,

    listTypeProd: [],
    isSchoolDisabled: true,
  }),

  computed: {
    canNavigateWizardFreely() {
      return this.option === 2 || this.option === 3;
    },
    divisa() {
      return this.$store.state.appConfig.divisa || "USD";
    },
    stockState() {
      return this.stockValid ? null : false;
    },
    durationMinutesState() {
      return this.durationMinutesValid ? null : false;
    },
  },
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.guidCompany = guid;
      if (this.guidCompany) {
        // this.loadCategory();
      }
    },
    "product.priceByRange"(newVal) {
      if (newVal === true) {
        // Cuando el precio es por rango
        this.product.price = 0;
        this.product.priceDivisa = 0;
      } else {
        // Cuando NO es por rango
        this.product.priceFrom = 0;
        this.product.priceTo = 0;
      }
    },
  },
  created() {
    this.guidCompany = this.$store.state.appConfig.activeCompanyGuid;
    localize("es");
    this.initialize();
  },

  mounted() {
    this.enableWizardFreeNavigation();
  },

  methods: {
    async initialize() {
      this.option = this.$route.params.option;

      if (!this.option) {
        this.$router.push({ name: "famcosProducts" });
        return;
      }

      this.onCancel();
      switch (this.option) {
        case 1:
          await this.showCatalog();
          break;
        case 2:
        case 3:
        case 4:
          if (this.$route.params.guid === undefined) {
            this.$router.push({ name: "famcosProducts" });
          }

          this.isSaveMode = this.option === 2 ? false : true;
          this.showCatalog();
          this.enableWizardFreeNavigation();
          // await Promise.all([
          //   this.showCatalog(),
          //   // this.loadCategory(),
          //   this.loadSchoolsInfinity(),
          // ]);

          break;
      }
    },

    enableWizardFreeNavigation() {
      if (!this.canNavigateWizardFreely) return;

      this.$nextTick(() => {
        if (this.$refs.form && this.$refs.form.activateAll) {
          this.$refs.form.activateAll();
        }
      });
    },

    async showCatalog() {
      try {
        //  CONDICION_CATALOGO
        const arrParamProdCondition = getParams([
          `itemsPage,1`,
          `itemsRows,999`,
          `guidCatalog,a5426eaf-8a96-42a2-a2b5-775fbc38fe09`,
        ]);

        //  FAMCOS_TIPO_PRODUCTO
        const arrParamTypeProd = getParams([
          `itemsPage,1`,
          `itemsRows,999`,
          `guidCatalog,129f350d-449e-40ce-a265-832bf7adee13`,
        ]);

        //  CATEGORIA_PRODUCTO
        const arrParamCategoryProducts = getParams([
          `page,1`,
          `rows,999`,
          `guidCompany,${this.guidCompany}`,
        ]);

        const [prodConditionResp, typeProdResp, respCategoryProducts] =
          await Promise.all([
            getCatalog(arrParamProdCondition),
            getCatalog(arrParamTypeProd),
            categoryProductsByCompany(arrParamCategoryProducts),

            this.loadSchoolsInfinity(),
          ]);

        const conditionData = _.get(prodConditionResp, "data") || [];
        this.prodCondition = conditionData.listOfItems.items;

        const typeProdData = _.get(typeProdResp, "data") || [];
        this.listTypeProd = typeProdData.listOfItems?.items;

        const categories = _.get(respCategoryProducts, "data.items");
        this.listCategories = categories;
        if (this.option != 1) this.showProduct(this.$route.params.guid);
      } catch (error) {
        console.log("error", error.message);
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      }
    },

    async showProduct(guid) {
      try {
        showLoading();
        await getProduct(guid)
          .then((resp) => {
            const product = _.get(resp, "data");
            this.product = Object.assign(this.product, {
              guid: product.guid || null,
              name: product.name || null,
              description: product.description || null,
              details: product.details || null,
              price: product.price || 0,
              priceDivisa: product.priceDivisa || 0,
              idCatCategory: product.idCatCategory || null,
              stock: product.stock || 0,
              photos: product.photos || [],
              idCatCondicion: product.idCatCondicion || null,
              priceByRange: product.priceByRange,
              priceFrom: product.priceFrom,
              priceTo: product.priceTo,
              stringCategoriasTags:
                this.stringToArray(product.stringCategoriasTags) || null,
              colegios: this.normalizeProductSchools(product),
              idCatFamcosTypeColegioCorporativo:
                product.idCatFamcosTypeColegioCorporativo,
              organizationObj:
                this.listTypeProd.find(
                  (item) =>
                    item.id === product.idCatFamcosTypeColegioCorporativo,
                ) || null,
              durationMinutes: product.durationMinutes || 0,
              generateAppointment: product.generateAppointment || false,
            });
            this.isSchoolDisabled = !(
              product.nameFamcosTypeColegioCorporativo.toLocaleLowerCase() ===
              String("Escolar").toLocaleLowerCase()
            );
            this.items = this.product.photos;

            if (this.option === 4) this.product.guid = null;
          })
          .catch((error) => {
            console.log("error", error.message);
            this.alertToast(
              "danger",
              "Error",
              error.message,
              "AlertTriangleIcon",
            );
          })
          .finally(() => {
            stopLoading();
          });
      } catch (error) {
        console.log("error", error.message);
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
      }
    },

    loadCategory() {
      try {
        const arrParams = getParams([
          `page,1`,
          `rows,999`,
          `guidCompany,${this.guidCompany}`,
        ]);
        categoryProductsByCompany(arrParams)
          .then((result) => {
            this.listCategories = _.get(result, "data.items");
          })
          .catch((error) => {
            console.log("error", error.message);
            this.alertToast("danger", `Alerta codigo`, error.message);
          });
      } catch (error) {
        console.log("error", error.message);
        this.alertToast("danger", `Alerta codigo`, error.message);
      }
    },

    async loadSchoolsInfinity() {
      try {
        if (this.schoolLoading) return;

        this.schoolLoading = true;

        const params = getParams([`page,1`, `rows,${this.schoolRows}`]);
        const resp = await allFamcosColegios(params);
        this.famcosColegiosList = _.get(resp, "data.items", []);
      } catch (error) {
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
        console.error("Error loading products infinitely", error);
      } finally {
        this.schoolLoading = false;
      }
    },

    organizationChanged(event) {
      if (
        event.name.toLocaleLowerCase() === String("Escolar").toLocaleLowerCase()
      ) {
        this.product.idCatFamcosTypeColegioCorporativo = event.id;
        this.isSchoolDisabled = false;
      } else {
        this.product.idCatFamcosTypeColegioCorporativo = event.id;
        this.isSchoolDisabled = true;
        this.product.idFamcosColegio = null;
        this.product.colegios = [];
      }
    },

    onSubmit() {
      this.$refs.productRules.validate().then((success) => {
        if (!success) {
          return;
        }
        const stringCategoriasTags = this.arrayToString(
          this.product.stringCategoriasTags || [],
        );
        showLoading();
        try {
          const data = {
            guid: this.product.guid,
            name: this.product.name,
            description: this.product.description,
            details: this.product.details,
            price: this.product.price,
            priceDivisa: this.product.priceDivisa,
            idCatCategory: this.product.idCatCategory,
            stock: this.product.stock,
            photos: this.product.photos,
            idCatCondicion: this.product.idCatCondicion,
            stringCategoriasTags: stringCategoriasTags,
            priceByRange: this.product.priceByRange,
            priceFrom: this.product.priceFrom,
            priceTo: this.product.priceTo,
            colegios: (this.product.colegios || []).map((idColegio) => ({
              idColegio,
            })),
            idCatFamcosTypeColegioCorporativo:
              this.product.idCatFamcosTypeColegioCorporativo,
            durationMinutes: this.product.durationMinutes,
            generateAppointment: this.product.generateAppointment,
          };

          if (this.option === 4) {
            data.photos = (this.product.photos || [])
              .filter((photo) => photo?.urlPhoto)
              .map((photo) => ({
                urlPhoto: photo.urlPhoto,
              }));
          }

          const products = [];
          products.push(data);

          const product = {
            products: products,
            guidCompany: this.guidCompany,
          };

          const guid =
            this.option === 1 || this.option === 4 ? null : this.product.guid;

          createOrEditProduct(
            this.option === 1 || this.option === 4 ? product : data,
            guid,
          )
            .then((resp) => {
              this.alertToast(
                "success",
                "Exito",
                "Transacción exitosa",
                "CheckIcon",
              );
              this.goBack();
            })
            .catch((error) => {
              console.log("error", error.message);
              this.alertToast("danger", "Error", error.message);
            })
            .finally(() => {
              stopLoading();
            });
        } catch (error) {
          console.log("error", error);
          this.alertToast(
            "danger",
            "Error",
            error?.message,
            "AlertTriangleIcon",
          );
        }
      });
    },

    label(field) {
      return `pages.products.form.label.${field}`;
    },
    getTitle() {
      if (this.option === 1) return "register";
      else if (this.option === 2) return "show";
      else if (this.option === 3) return "edit";
      else if (this.option === 4) return "clone";
    },
    validateStock() {
      const regex = /^(0|[1-9][0-9]*)$/;
      const inputValue = this.product.stock.trim();

      if (!regex.test(inputValue)) {
        this.stockValid = false;
        this.product.stock = 0;
      } else {
        this.stockValid = true;
      }
    },
    onInput(newValue) {
      const sanitized = newValue.replace(/\D/g, "");
      this.product.stock = sanitized;

      this.stockValid = true;
    },
    onBlur() {
      const regex = /^(0|[1-9]\d*)$/;
      const inputValue = this.product.stock;

      if (regex.test(inputValue)) {
        this.stockValid = true;
      } else {
        this.product.stock = "0";
        this.stockValid = false;
      }
    },

    onInputDurationMinutes(newValue) {
      const sanitized = newValue.replace(/\D/g, "");
      this.product.durationMinutes = sanitized;

      this.durationMinutesValid = true;
    },
    onBlurDurationMinutes() {
      const regex = /^(0|[1-9]\d*)$/;
      const inputValue = this.product.durationMinutes;

      if (regex.test(inputValue)) {
        this.durationMinutesValid = true;
      } else {
        this.product.durationMinutes = "0";
        this.durationMinutesValid = false;
      }
    },
    handleDragover(e) {
      e.stopPropagation();
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },

    handleDrop(e, field) {
      e.stopPropagation();
      e.preventDefault();
      const { files } = e.dataTransfer;
      if (files.length !== 1) {
        return;
      }
      const accept =
        e.target.getAttribute("accept") ||
        e.target.parentElement.getAttribute("accept");
      const multiple =
        e.target.getAttribute("multiple") ||
        e.target.parentElement.getAttribute("multiple");
      this.onTransFile(files, accept, multiple, field);
    },

    onFileChange(e, field) {
      if (e?.target?.files?.length > 0) {
        this.onTransFile(
          e?.target?.files,
          e.target.accept,
          e.target.multiple,
          field,
        );
      }
    },

    onTransFile(files, accept, multiple, field) {
      try {
        if (!multiple) {
          files.forEach((file) => {
            if (file) {
              const type = file.type.split("/").pop();
              if (file && accept.split(",").indexOf(`.${type}`) > -1) {
                showLoading();
                postFile(file)
                  .then((resp) => {
                    stopLoading();
                    this.product.photos.push({
                      urlPhoto: resp.data,
                    });
                  })
                  .catch((error) => {
                    stopLoading();
                  });
              }
            }
          });
        }
      } catch (error) {
        console.error(error);
      }
    },

    deleteFile(item, index) {
      this.product.photos = _.reject(this.product.photos, {
        urlPhoto: item.urlPhoto,
      });
    },

    onCancel() {
      this.product = Object.assign({
        guid: null,
        name: null,
        description: null,
        details: null,
        price: 0,
        priceDivisa: 0,
        idCatCategory: null,
        stock: 0,
        idIva: null,
        idCatCondicion: null,
        stringCategoriasTags: null,
        colegios: [],
        folio: null,
        photos: [],
        priceByRange: false,
        priceFrom: 0,
        priceTo: 0,
        idFamcosColegio: null,
        idCatFamcosTypeColegioCorporativo: null,
        durationMinutes: 0,
        generateAppointment: false,
        organizationObj: null,
      });

      this.prodCondition = [];
      this.listTypeProd = [];
      this.isSchoolDisabled = true;
    },

    validationProduct() {
      return new Promise((resolve, reject) => {
        this.$refs.productRules.validate().then((success) => {
          if (success) {
            resolve(true);
          } else {
            reject();
          }
        });
      });
    },

    validationProductTab() {
      if (this.canNavigateWizardFreely) return true;

      return this.validationProduct();
    },

    alertToast(type = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon: "CoffeeIcon",
      });
    },

    goBack() {
      this.$refs.productRules.reset();
      router.back();
    },

    arrayToString(array) {
      return array.join(", ");
    },

    stringToArray(str) {
      if (str !== null) {
        return str.split(", ");
      } else {
        return null;
      }
    },

    normalizeProductSchools(product) {
      const currentSchools = Array.isArray(product.colegios)
        ? product.colegios
        : [];
      let schoolIds = currentSchools
        .map((school) =>
          typeof school === "object" ? school.idColegio : school,
        )
        .filter((idColegio) => idColegio !== null && idColegio !== undefined);

      if (
        schoolIds.length === 0 &&
        product.idFamcosColegio !== null &&
        product.idFamcosColegio !== undefined
      ) {
        schoolIds = [product.idFamcosColegio];
      }

      return schoolIds
        .map((idColegio) =>
          this.famcosColegiosList.find(
            (school) => String(school.idColegio) === String(idColegio),
          ),
        )
        .filter(Boolean)
        .map((school) => school.idColegio);
    },

    changeRange(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    changeGenerateAppointment(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    isPriceByRange(value) {
      return value ? "Si" : "No";
    },

    isGenerateAppointment(value) {
      return value ? "Si" : "No";
    },
  },
};
</script>

<style lang="scss">
@import "../../../../../public/loader.css";
@import "@core/scss/vue/libs/vue-select.scss";
@import "~@core/scss/vue/libs/vue-wizard.scss";
</style>
